import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { toListQuery } from './filters';
import {
  warrantyClaimDetailSchema,
  warrantyClaimListResponseSchema,
  warrantyDeviceOptionPageSchema,
} from './schemas';
import type {
  RejectWarrantyClaimValues,
  WarrantyClaimDetail,
  WarrantyClaimListParams,
  WarrantyClaimListResponse,
  WarrantyDeviceOptionPage,
  WarrantyDeviceOptionParams,
} from './types';

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function warrantyClaimsRequestQuery(
  params: Omit<WarrantyClaimListParams, 'page' | 'limit'>,
) {
  const { sortBy = 'claimDate', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy,
    sortOrder: sortOrder.toUpperCase(),
    ...toListQuery(filters),
  };
}

/** Paginated/sorted/filtered/searchable warranty-claim list — one row per claim (one device).
 *  Real contract: `POST .../query` with a JSON filter body, like both sibling queues. Defaults to
 *  newest Warranty Claim Date first (AC: default sort `claimDate desc`). `sortOrder` is uppercased
 *  for the DTO's `SortOrder` enum. */
export async function listWarrantyClaims(
  params: WarrantyClaimListParams,
): Promise<WarrantyClaimListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.postList(endpoints.requests.warranty.query, {
    page,
    limit,
    ...warrantyClaimsRequestQuery(rest),
  });
  return warrantyClaimListResponseSchema.parse({ data, meta });
}

/** One warranty claim's full detail (`WarrantyClaimDetailResponse`) — item-centric: the device, its
 *  evidence images, the activity timeline and, when rejected, the admin's reason. It carries no
 *  customer/order/date context, so the queue page merges this with the row it already holds. */
export async function getWarrantyClaim(id: string): Promise<WarrantyClaimDetail> {
  return warrantyClaimDetailSchema.parse(
    await apiClient.get(endpoints.requests.warranty.detail(id)),
  );
}

/** Approves a Pending warranty claim. The backend records the decision and notifies the customer; it
 *  creates **no Order and no Installation** (grilling decision 15), which is why there is no request
 *  body and nothing to assign. Real contract: `PATCH`, returning only `{id, status}` — the caller
 *  invalidates rather than seeding the detail cache. */
export async function approveWarrantyClaim(id: string): Promise<void> {
  await apiClient.patch(endpoints.requests.warranty.approve(id));
}

/** Rejects a Pending warranty claim with the admin's reason. The backend saves it as the claim's
 *  Rejection Reason and notifies the customer; a decision creates **no order and no installation**
 *  (grilling decision 15). Real contract: `PATCH`, returning only `{id, status}`.
 *  Customer-notification delivery is fire-and-forget: the admin is never told when it fails
 *  (BA on 110179, 2026-07-28), so the response carries no notification signal. */
export async function rejectWarrantyClaim(
  id: string,
  values: RejectWarrantyClaimValues,
): Promise<void> {
  await apiClient.patch(endpoints.requests.warranty.reject(id), values);
}

/** One page of the Device filter's server-searched typeahead.
 *
 *  It asks the shared `/products/admin/meta/devices` endpoint **unscoped** — the same call the
 *  Replacements queue makes. Before ADO 118533 this dropdown borrowed `useMetaDevices` from the
 *  bundles feature, which sends `status=active&bundleEligible=true` (the bundle create/edit form's
 *  own scope) and caps the list at one 100-row page: every device that is not bundle-eligible, not
 *  Active, or past the first 100 was silently absent from a filter whose rows those very devices
 *  appear in. The backend defaults both flags off (`GetDeviceProductMetaDto`), so omitting them
 *  returns the whole catalog. */
export async function listWarrantyDevices({
  search,
  cursor,
  limit,
}: WarrantyDeviceOptionParams): Promise<WarrantyDeviceOptionPage> {
  const page = await apiClient.getList(endpoints.requests.warranty.meta.devices, {
    params: { search, limit, ...(cursor ? { cursor } : {}) },
  });
  return warrantyDeviceOptionPageSchema.parse({
    ...page,
    meta: page.meta ?? { hasNextPage: false },
  });
}
